// Directorio unificado de políticos nicoleños:
// - cruza hcd_authority_profiles (concejales con foto) + investigation_entities
//   (personas con perfil investigativo de dossiers).
// - dedupea por slug normalizado.
// - grid de cards con foto / nombre / cargo / partido / badges.
// - click → navega a /investigations/politico/:slug (PoliticianProfilePage), una
//   sección con perfil completo + dossiers + conexiones + chat IA (antes era popup).

import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaUserCircle,
  FaSearch,
  FaTimes,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaRobot,
  FaUser,
  FaPaperPlane,
  FaTrashAlt,
  FaCommentDots,
  FaBalanceScale,
  FaIdBadge,
  FaProjectDiagram,
  FaBuilding,
  FaArrowLeft,
} from 'react-icons/fa';
import {
  TechInput,
  TechChip,
  TechButton,
  Loading,
  ErrorBox,
  Empty,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  listPoliticians,
  getPolitician,
  getPoliticianChatHistory,
  sendPoliticianChat,
  clearPoliticianChatHistory,
} from '../../services/investigationService';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.9rem;
`;

const PoliticianCard = styled.button`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  padding: 0.95rem 0.9rem 0.9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  cursor: pointer;
  text-align: center;
  transition: all 0.18s ease;
  position: relative;

  &:hover {
    border-color: rgba(0, 180, 216, 0.5);
    background: rgba(0, 180, 216, 0.04);
    transform: translateY(-2px);
  }

  .photo {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(11, 19, 32, 0.7);
    border: 2px solid ${borderSoft};
    display: flex; align-items: center; justify-content: center;
    color: var(--text-disabled);
    font-size: 2.2rem;
    flex-shrink: 0;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .name {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.92rem;
    line-height: 1.25;
    margin-top: 2px;
  }
  .role {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    line-height: 1.35;
    min-height: 1.5em;
  }
  .party {
    color: var(--text-secondary);
    font-size: 0.7rem;
    line-height: 1.3;
  }
  .badges {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 2px;
  }
  .badge {
    font-family: ${mono};
    font-size: 0.55rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid ${borderSoft};
    background: rgba(11, 19, 32, 0.5);
    color: var(--text-disabled);
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  .badge-warn {
    color: var(--alert-warning, #f4a261);
    background: rgba(244, 162, 97, 0.1);
    border-color: rgba(244, 162, 97, 0.35);
  }
  .badge-shared {
    color: ${accent};
    background: rgba(0, 180, 216, 0.08);
    border-color: rgba(0, 180, 216, 0.35);
  }
`;

const Modal = styled.div`
  position: fixed; inset: 0; z-index: 1600;
  background: rgba(8, 14, 24, 0.7);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
`;
const ModalCard = styled.div`
  width: min(940px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 12px;
  position: relative;
  overflow: hidden;

  .close {
    position: absolute; top: 14px; right: 16px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 30px; height: 30px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    z-index: 2;
    &:hover { color: var(--alert-error); }
  }
  .header {
    padding: 1.3rem 1.4rem 0.85rem;
    display: flex;
    gap: 1rem;
    align-items: center;
    border-bottom: 1px solid ${borderSoft};
    .photo {
      width: 84px; height: 84px;
      border-radius: 50%;
      overflow: hidden;
      background: rgba(11, 19, 32, 0.7);
      border: 2px solid ${borderSoft};
      flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      color: var(--text-disabled);
      font-size: 2rem;
      img { width: 100%; height: 100%; object-fit: cover; }
    }
    .meta {
      flex: 1; min-width: 0;
      h2 {
        margin: 0 0 4px;
        color: var(--text-primary);
        font-size: 1.25rem;
        line-height: 1.25;
      }
      .role {
        font-family: ${mono};
        font-size: 0.65rem;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--text-disabled);
        margin-bottom: 4px;
      }
      .party {
        color: var(--text-secondary);
        font-size: 0.86rem;
        margin-bottom: 2px;
      }
      .chips {
        display: flex; gap: 5px; flex-wrap: wrap; margin-top: 6px;
      }
    }
  }
  .body {
    flex: 1;
    overflow-y: auto;
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) 360px;
    @media (max-width: 980px) { grid-template-columns: 1fr; }
  }
  .col-left {
    padding: 1.1rem 1.4rem;
    overflow-y: auto;
    border-right: 1px solid ${borderSoft};
    @media (max-width: 980px) { border-right: none; }
  }
  .col-right {
    padding: 1.1rem 1.2rem 1.2rem;
    background: rgba(11, 19, 32, 0.25);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .section-h {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin: 0 0 0.55rem;
    display: flex; align-items: center; gap: 6px;
  }
  .bio {
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-left: 2px solid ${accent};
    border-radius: 6px;
    padding: 0.7rem 0.85rem;
    color: var(--text-secondary);
    font-size: 0.88rem;
    line-height: 1.55;
    margin-bottom: 1rem;
  }
  .dossier-link {
    display: flex; align-items: center; gap: 9px;
    padding: 9px 11px;
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    text-decoration: none;
    color: var(--text-secondary);
    font-size: 0.86rem;
    margin-bottom: 5px;
    transition: all 0.15s ease;
    &:hover { border-color: rgba(0, 180, 216, 0.45); color: var(--text-primary); }
    .swatch { width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }
    .title-wrap { flex: 1; min-width: 0; }
    strong { color: var(--text-primary); display: block; }
    .resume {
      font-size: 0.78rem;
      color: var(--text-disabled);
      margin-top: 1px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .badges { display: flex; gap: 4px; flex-shrink: 0; }
    .badge {
      font-family: ${mono};
      font-size: 0.54rem;
      letter-spacing: 0.12em;
      padding: 1px 5px;
      border-radius: 4px;
      border: 1px solid;
    }
    .badge.warn { color: var(--alert-warning, #f4a261); background: rgba(244, 162, 97, 0.08); border-color: rgba(244, 162, 97, 0.4); }
    .badge.conf { color: ${accent}; background: rgba(0, 180, 216, 0.06); border-color: rgba(0, 180, 216, 0.4); }
  }
  .connection-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 10px;
    background: rgba(11, 19, 32, 0.4);
    border: 1px solid ${borderSoft};
    border-radius: 6px;
    margin-bottom: 5px;
    font-size: 0.84rem;
    .arr { color: var(--text-disabled); }
    .name { color: var(--text-primary); font-weight: 600; }
    .kind {
      font-family: ${mono};
      font-size: 0.58rem;
      letter-spacing: 0.13em;
      text-transform: uppercase;
      padding: 2px 6px;
      border-radius: 4px;
      border: 1px solid;
    }
    .desc {
      color: var(--text-disabled);
      font-size: 0.75rem;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .denuncias-list, .conflictos-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0.8rem;
  }
  .denuncia-item, .conflicto-item {
    background: rgba(239, 71, 111, 0.04);
    border: 1px solid rgba(239, 71, 111, 0.25);
    border-radius: 6px;
    padding: 8px 11px;
    margin-bottom: 5px;
    font-size: 0.84rem;
    color: var(--text-secondary);
    line-height: 1.5;
    .meta {
      font-family: ${mono};
      font-size: 0.58rem;
      letter-spacing: 0.13em;
      text-transform: uppercase;
      color: var(--text-disabled);
      margin-bottom: 3px;
    }
  }
  .conflicto-item { background: rgba(244, 162, 97, 0.05); border-color: rgba(244, 162, 97, 0.3); }
`;

// Contenedor de página (cuando el detalle se muestra como sección, no popup).
const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;
const BackLink = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(0, 180, 216, 0.45); color: var(--text-primary); }
`;

// Envuelve el detalle como overlay (modal) o como sección de página según asPage.
function Shell({ asPage, onClose, children }) {
  if (asPage) {
    return (
      <PageWrap>
        <BackLink type="button" onClick={onClose}><FaArrowLeft /> Volver al directorio</BackLink>
        {children}
      </PageWrap>
    );
  }
  return <Modal onClick={onClose}>{children}</Modal>;
}

const KIND_COLOR = {
  family:      '#ef476f',
  business:    '#f4a261',
  political:   '#00b4d8',
  employment:  '#a78bfa',
  financial:   '#06d6a0',
  legal:       '#fbbf24',
  social:      '#9ca3af',
  historical:  '#7e7e7e',
  rivalry:     '#ef4444',
  other:       '#6b7280',
};

const ChatWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  .chat-head {
    display: flex; align-items: center; gap: 6px;
    margin-bottom: 8px;
    .title {
      flex: 1;
      font-family: ${mono};
      font-size: 0.66rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-secondary);
    }
    .clear {
      background: transparent;
      border: 1px solid ${borderSoft};
      color: var(--text-disabled);
      padding: 3px 7px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 0.7rem;
      &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
    }
  }
  .chat-body {
    flex: 1;
    min-height: 200px;
    max-height: 460px;
    overflow-y: auto;
    background: rgba(11, 19, 32, 0.3);
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    padding: 0.5rem 0.4rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .empty {
    padding: 0.7rem;
    color: var(--text-disabled);
    font-size: 0.82rem;
    line-height: 1.5;
  }
  .suggest {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-secondary);
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.76rem;
    margin-top: 5px;
    cursor: pointer;
    &:hover { border-color: rgba(0, 180, 216, 0.4); background: rgba(0, 180, 216, 0.04); }
  }
  .msg {
    display: flex; gap: 7px;
    padding: 0 0.25rem;
    .avatar {
      width: 24px; height: 24px;
      border-radius: 50%;
      flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(11, 19, 32, 0.7);
      border: 1px solid ${borderSoft};
      font-size: 0.65rem;
      color: var(--text-disabled);
    }
    &.assistant .avatar { color: ${accent}; }
    .bubble {
      background: rgba(11, 19, 32, 0.55);
      border: 1px solid ${borderSoft};
      padding: 6px 10px;
      border-radius: 8px;
      color: var(--text-secondary);
      font-size: 0.84rem;
      line-height: 1.5;
      word-break: break-word;
      p, ul { margin: 3px 0; }
    }
    .actions-pill {
      margin-top: 4px;
      display: inline-block;
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.14em;
      color: #62d59a;
      background: rgba(98, 213, 154, 0.07);
      border: 1px solid rgba(98, 213, 154, 0.3);
      padding: 2px 7px;
      border-radius: 4px;
    }
  }
  .input-wrap {
    display: flex; gap: 5px;
    margin-top: 7px;
    textarea {
      flex: 1;
      background: rgba(11, 19, 32, 0.6);
      border: 1px solid ${borderSoft};
      border-radius: 7px;
      color: var(--text-primary);
      font-family: inherit;
      padding: 7px 10px;
      font-size: 0.85rem;
      resize: none;
      min-height: 36px;
      max-height: 100px;
      &:focus { outline: none; border-color: rgba(0, 180, 216, 0.5); }
    }
    button {
      width: 36px; height: 36px;
      border-radius: 6px;
      border: 1px solid rgba(0, 180, 216, 0.35);
      background: rgba(0, 180, 216, 0.15);
      color: ${accent};
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      &:disabled { opacity: 0.45; cursor: not-allowed; }
      &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); }
    }
  }
`;

function formatTitleCase(name) {
  if (!name) return '';
  return name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function PoliticianDetailView({ slug, onClose, asPage = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatErr, setChatErr] = useState(null);
  const chatBodyRef = React.useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const p = await getPolitician(slug);
      setData(p);
    } catch (e) {
      setErr(friendlyError(e));
    } finally { setLoading(false); }
  };

  const loadChat = async () => {
    try {
      const m = await getPoliticianChatHistory(slug);
      setChatHistory(m);
    } catch (e) {
      console.warn('politician chat history err', e.message);
    }
  };

  useEffect(() => {
    reload();
    loadChat();
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [chatHistory, chatSending]);

  const send = async (override) => {
    const q = (override ?? chatInput).trim();
    if (!q || chatSending) return;
    setChatErr(null);
    setChatHistory((h) => [...h, { role: 'user', content: q, created_at: new Date().toISOString() }]);
    setChatInput('');
    setChatSending(true);
    try {
      await sendPoliticianChat(slug, q);
      await loadChat();
    } catch (e) {
      setChatErr(friendlyError(e));
    } finally { setChatSending(false); }
  };

  const clearChat = async () => {
    if (!window.confirm('¿Borrar historial del chat de este político?')) return;
    await clearPoliticianChatHistory(slug);
    setChatHistory([]);
  };

  if (loading || !data) {
    return (
      <Shell asPage={asPage} onClose={onClose}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <button className="close" onClick={onClose}><FaTimes /></button>
          {loading ? <div style={{ padding: '2rem' }}><Loading>Cargando perfil…</Loading></div>
            : <div style={{ padding: '2rem' }}><ErrorBox>{err || 'No encontrado'}</ErrorBox></div>}
        </ModalCard>
      </Shell>
    );
  }

  const hcd = data.hcd_profile || {};
  const bio = hcd.biografia || data.mentions?.[0]?.summary || null;
  // Junto denuncias y conflictos de todos los dossier_profiles
  const allDenuncias = [];
  const allConflictos = [];
  const allRedEmpresarial = [];
  const allRedFamiliar = [];
  for (const ep of (data.entity_profiles || [])) {
    const p = ep.profile || {};
    for (const d of (p.denuncias_y_causas_judiciales || [])) allDenuncias.push(d);
    for (const c of (p.conflictos_de_interes || [])) allConflictos.push(c);
    for (const r of (p.red_empresarial || [])) allRedEmpresarial.push(r);
    for (const f of (p.red_familiar_y_designaciones || [])) allRedFamiliar.push(f);
  }

  const SUGGESTIONS = [
    `¿Hay denuncias o causas judiciales recientes contra ${data.name}?`,
    `Buscá en el HCD qué proyectos presentó o votó.`,
    `Resumime sus principales conflictos de interés y red empresarial.`,
  ];

  return (
    <Shell asPage={asPage} onClose={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose} title={asPage ? 'Volver' : 'Cerrar'}>
          {asPage ? <FaArrowLeft /> : <FaTimes />}
        </button>

        <div className="header">
          <div className="photo">
            {data.photo_url
              ? <img src={data.photo_url} alt={data.name} onError={(e) => { e.target.style.display = 'none'; }} />
              : <FaUserCircle />}
          </div>
          <div className="meta">
            <h2>{formatTitleCase(data.name)}</h2>
            {data.role && <div className="role">{data.role}</div>}
            {data.party && <div className="party">{data.party}</div>}
            <div className="chips">
              {data.period && <TechChip>{data.period}</TechChip>}
              {data.dossier_count > 0 && <TechChip>{data.dossier_count} dossier{data.dossier_count === 1 ? '' : 's'}</TechChip>}
              {data.connection_count > 0 && <TechChip>{data.connection_count} conex.</TechChip>}
              {data.has_denuncias && (
                <TechChip tone="warn"><FaExclamationTriangle /> denuncias</TechChip>
              )}
              {data.email && <TechChip>{data.email}</TechChip>}
            </div>
          </div>
        </div>

        <div className="body">
          <div className="col-left">
            {bio && (
              <>
                <div className="section-h"><FaIdBadge /> Perfil</div>
                <div className="bio">{bio}</div>
              </>
            )}

            {Array.isArray(hcd.trayectoria_resumen) && hcd.trayectoria_resumen.length > 0 && (
              <>
                <div className="section-h">Trayectoria</div>
                <ul style={{ margin: '0 0 1rem', paddingLeft: 22, color: 'var(--text-secondary)', fontSize: '0.86rem', lineHeight: 1.55 }}>
                  {hcd.trayectoria_resumen.slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </>
            )}

            {data.mentions && data.mentions.length > 0 && (
              <>
                <div className="section-h"><FaProjectDiagram /> Dossiers donde aparece ({data.mentions.length})</div>
                {data.mentions.map((m) => (
                  <Link
                    key={m.entity_id}
                    to={`/investigations/${m.investigation_slug}`}
                    className="dossier-link"
                    onClick={asPage ? undefined : onClose}
                  >
                    <span className="swatch" style={{ background: m.cover_color || accent }} />
                    <div className="title-wrap">
                      <strong>{m.investigation_title}</strong>
                      {m.summary && <div className="resume">{m.summary}</div>}
                    </div>
                    <div className="badges">
                      {m.has_denuncias && <span className="badge warn">denuncias</span>}
                      {m.connection_count > 0 && <span className="badge conf">{m.connection_count} conex.</span>}
                    </div>
                  </Link>
                ))}
              </>
            )}

            {allDenuncias.length > 0 && (
              <>
                <div className="section-h"><FaBalanceScale /> Denuncias y causas ({allDenuncias.length})</div>
                <ul className="denuncias-list">
                  {allDenuncias.map((d, i) => (
                    <li key={i} className="denuncia-item">
                      <div className="meta">
                        {d.fuente_tipo || 'denuncia'}{d.fecha ? ` · ${d.fecha}` : ''}{d.estado ? ` · ${d.estado}` : ''}
                      </div>
                      {d.descripcion}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {allConflictos.length > 0 && (
              <>
                <div className="section-h">Conflictos de interés ({allConflictos.length})</div>
                <ul className="conflictos-list">
                  {allConflictos.map((c, i) => (
                    <li key={i} className="conflicto-item">{c.descripcion}</li>
                  ))}
                </ul>
              </>
            )}

            {allRedEmpresarial.length > 0 && (
              <>
                <div className="section-h"><FaBuilding /> Red empresarial ({allRedEmpresarial.length})</div>
                <ul style={{ margin: '0 0 1rem', paddingLeft: 22, color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.55 }}>
                  {allRedEmpresarial.map((e, i) => (
                    <li key={i}>
                      <strong>{e.entidad}</strong>{e.rol ? ` — ${e.rol}` : ''}{e.actividad ? ` · ${e.actividad}` : ''}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {allRedFamiliar.length > 0 && (
              <>
                <div className="section-h">Red familiar / designaciones ({allRedFamiliar.length})</div>
                <ul style={{ margin: '0 0 1rem', paddingLeft: 22, color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.55 }}>
                  {allRedFamiliar.map((f, i) => (
                    <li key={i}>
                      <strong>{f.persona}</strong>{f.vinculo ? ` (${f.vinculo})` : ''}{f.cargo ? ` — ${f.cargo}` : ''}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {data.connections && data.connections.length > 0 && (
              <>
                <div className="section-h">Conexiones ({data.connections.length})</div>
                {data.connections.slice(0, 18).map((c, i) => {
                  const myEntityIds = new Set((data.mentions || []).map((m) => m.entity_id));
                  const isFrom = myEntityIds.has(c.from_entity_id);
                  const otherName = isFrom ? c.to_name : c.from_name;
                  const kindColor = KIND_COLOR[c.kind] || '#6b7280';
                  return (
                    <div key={i} className="connection-row">
                      <span className="arr">↔</span>
                      <span className="name">{otherName}</span>
                      <span className="kind" style={{ color: kindColor, borderColor: `${kindColor}66`, background: `${kindColor}15` }}>
                        {c.kind}
                      </span>
                      {c.label && <span className="desc">{c.label}</span>}
                    </div>
                  );
                })}
              </>
            )}

            {!bio && data.dossier_count === 0 && (
              <Empty>
                Este político fue cargado desde el HCD pero todavía no aparece en ningún dossier. Charlá con la IA → puede crear un dossier sobre él o buscar denuncias en Google News.
              </Empty>
            )}
          </div>

          <div className="col-right">
            <ChatWrap>
              <div className="chat-head">
                <FaRobot style={{ color: accent }} />
                <span className="title">Chat con IA</span>
                {chatHistory.length > 0 && (
                  <button type="button" className="clear" onClick={clearChat} title="Borrar historial">
                    <FaTrashAlt />
                  </button>
                )}
              </div>
              <div className="chat-body" ref={chatBodyRef}>
                {chatHistory.length === 0 && !chatSending && (
                  <div className="empty">
                    Indagá el perfil. La IA tiene acceso al perfil HCD, los dossiers donde aparece y el corpus indexado. Puede buscar evidencia en HCD y scrapear web.
                    {SUGGESTIONS.map((s) => (
                      <button key={s} type="button" className="suggest" onClick={() => send(s)}>{s}</button>
                    ))}
                  </div>
                )}
                {chatHistory.map((m) => (
                  <div key={m.id || m.created_at} className={`msg ${m.role}`}>
                    <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="bubble">
                        {m.role === 'assistant'
                          ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                          : m.content}
                      </div>
                      {Array.isArray(m.actions) && m.actions.length > 0 && (
                        <div className="actions-pill">
                          ✓ {m.actions.filter((a) => a.ok).map((a) => a.tool).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {chatSending && (
                  <div className="msg assistant">
                    <div className="avatar"><FaRobot /></div>
                    <div className="bubble" style={{ color: 'var(--text-disabled)' }}>investigando…</div>
                  </div>
                )}
                {chatErr && <ErrorBox>{chatErr}</ErrorBox>}
              </div>
              <form className="input-wrap" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  placeholder="Indagá sobre este político…"
                  disabled={chatSending}
                  rows={1}
                />
                <button type="submit" disabled={chatSending || !chatInput.trim()} aria-label="Enviar">
                  <FaPaperPlane />
                </button>
              </form>
            </ChatWrap>
          </div>
        </div>
      </ModalCard>
    </Shell>
  );
}

export default function PoliticiansDirectory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | with_photo | in_dossier | with_denuncias
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await listPoliticians();
        if (!cancelled) setItems(r);
      } catch (e) {
        if (!cancelled) setErr(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((p) => {
      if (filter === 'with_photo' && !p.photo_url_real) return false;
      if (filter === 'in_dossier' && !(p.dossier_count > 0)) return false;
      if (filter === 'with_denuncias' && !p.has_denuncias) return false;
      if (q) {
        return (
          (p.name || '').toLowerCase().includes(q) ||
          (p.role || '').toLowerCase().includes(q) ||
          (p.party || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [items, query, filter]);

  const totals = useMemo(() => ({
    total: items.length,
    with_photo: items.filter((p) => p.photo_url_real).length,
    in_dossier: items.filter((p) => p.dossier_count > 0).length,
    with_denuncias: items.filter((p) => p.has_denuncias).length,
  }), [items]);

  if (loading) return <Loading>Cargando políticos…</Loading>;
  if (err) return <ErrorBox>{err}</ErrorBox>;
  if (items.length === 0) {
    return (
      <Empty>
        Todavía no hay políticos cargados. Corré el cron de autoridades HCD o agregá entidades de tipo "person" a un dossier.
      </Empty>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.9rem' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)', fontSize: '0.82rem' }} />
          <TechInput
            placeholder="Buscar por nombre, cargo o partido…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
        {[
          { id: 'all', label: `Todos · ${totals.total}` },
          { id: 'with_photo', label: `Con foto · ${totals.with_photo}` },
          { id: 'in_dossier', label: `En dossier · ${totals.in_dossier}` },
          { id: 'with_denuncias', label: `⚠ Denuncias · ${totals.with_denuncias}` },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            style={{
              background: filter === f.id ? 'rgba(0, 180, 216, 0.15)' : 'transparent',
              color: filter === f.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: '1px solid ' + (filter === f.id ? 'rgba(0, 180, 216, 0.4)' : borderSoft),
              padding: '0.4rem 0.85rem',
              borderRadius: 999,
              fontSize: '0.76rem',
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Empty>Sin resultados con esos filtros.</Empty>
      ) : (
        <Grid>
          {filtered.map((p) => (
            <PoliticianCard key={p.slug} onClick={() => navigate(`/investigations/politico/${p.slug}`)}>
              <div className="photo">
                {p.photo_url
                  ? <img src={p.photo_url} alt={p.name} onError={(e) => { e.target.style.display = 'none'; }} />
                  : <FaUserCircle />}
              </div>
              <div className="name">{formatTitleCase(p.name)}</div>
              {p.role && <div className="role">{p.role}</div>}
              {p.party && p.party !== 'NINGUNO' && <div className="party">{p.party}</div>}
              <div className="badges">
                {p.dossier_count > 0 && <span className="badge badge-shared">{p.dossier_count} dossier{p.dossier_count === 1 ? '' : 's'}</span>}
                {p.has_denuncias && <span className="badge badge-warn">⚠ denuncias</span>}
                {p.connection_count > 0 && <span className="badge">{p.connection_count} conex.</span>}
              </div>
            </PoliticianCard>
          ))}
        </Grid>
      )}
    </>
  );
}
